import { useEffect, useMemo, useState } from 'react';

import {
  getTocScrollOffsetPx,
  SCROLL_CORRECTION_TOLERANCE_PX,
} from './scrollOffsets';

function resolveActiveId(entryIds: string[]) {
  if (typeof document === 'undefined' || entryIds.length === 0) {
    return null;
  }

  // A contents link calls its heading on target within this tolerance, and
  // scroll positions are whole pixels: a heading it brought to the line can
  // stop a fraction of a pixel below it, which left the entry above current.
  const threshold = getTocScrollOffsetPx() + SCROLL_CORRECTION_TOLERANCE_PX;
  let activeId: string | null = null;
  let nearestPositiveId: string | null = null;
  let nearestPositiveTop = Number.POSITIVE_INFINITY;

  entryIds.forEach((entryId) => {
    const target = document.getElementById(entryId);

    if (!target) {
      return;
    }

    const { top } = target.getBoundingClientRect();

    if (top <= threshold) {
      activeId = entryId;
      return;
    }

    if (top < nearestPositiveTop) {
      nearestPositiveTop = top;
      nearestPositiveId = entryId;
    }
  });

  return activeId ?? nearestPositiveId ?? entryIds[0] ?? null;
}

export function useActiveHeading(entryIds: string[]) {
  const [activeId, setActiveId] = useState<string | null>(entryIds[0] ?? null);
  const joinedEntryIds = useMemo(() => entryIds.join('|'), [entryIds]);

  useEffect(() => {
    setActiveId(entryIds[0] ?? null);
  }, [joinedEntryIds, entryIds]);

  useEffect(() => {
    if (typeof window === 'undefined' || entryIds.length === 0) {
      return undefined;
    }

    const updateActiveId = () => {
      setActiveId(resolveActiveId(entryIds));
    };

    updateActiveId();

    const targets = entryIds
      .map((entryId) => document.getElementById(entryId))
      .filter((target): target is HTMLElement => target instanceof HTMLElement);

    if (targets.length === 0) {
      return undefined;
    }

    window.addEventListener('scroll', updateActiveId, { passive: true });
    window.addEventListener('resize', updateActiveId);

    if (typeof IntersectionObserver === 'undefined') {
      return () => {
        window.removeEventListener('scroll', updateActiveId);
        window.removeEventListener('resize', updateActiveId);
      };
    }

    const observer = new IntersectionObserver(updateActiveId, {
      rootMargin: '-20% 0px -55% 0px',
      threshold: [0, 0.25, 0.5, 1],
    });

    targets.forEach((target) => {
      observer.observe(target);
    });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateActiveId);
      window.removeEventListener('resize', updateActiveId);
    };
  }, [entryIds, joinedEntryIds]);

  return activeId;
}
